import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { parseISO } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { TZDate } from '@date-fns/tz';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { TitleBar } from '../../components/TitleBar';
import InputWithLabel from '../../components/InputWithLabel';
import { useAuth } from '../../hooks/useAuth';
import { eventsRepository, EventUpdateDto } from '../../repositories/EventsRepository';
import { Event } from '../../models/Event';
import { Button, ButtonSize } from '../../components/Button';
import { ButtonVariant } from '../../config/colors';
import { useConfirm } from '../../hooks/useConfirm';
import Loader from './components/Loader';

const TIME_STEP_MINUTES = 30;

function getDefaultTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

interface FormEditEventProps {}

interface EventFormData {
  name: string;
  startDate: string;
  endDate: string;
  timeZone: string;
  venue: string;
  address: string;
  latitude: string;
  longitude: string;
  description: string;
}

const FormEditEvent: React.FC<FormEditEventProps> = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const { pleb } = useAuth();
  const { openConfirm } = useConfirm();
  const placeWrapperRef = useRef<HTMLDivElement>(null);
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [placeSearchValue, setPlaceSearchValue] = useState('');
  const [showPlaceResults, setShowPlaceResults] = useState(false);
  const [placePredictions, setPlacePredictions] = useState<{ placeId: string; name: string }[]>([]);
  const [formData, setFormData] = useState<EventFormData>({
    name: '',
    startDate: '',
    endDate: '',
    timeZone: getDefaultTimeZone(),
    venue: '',
    address: '',
    latitude: '',
    longitude: '',
    description: '',
  });

  useEffect(() => {
    if (!eventId || !pleb?.id) return;
    setLoading(true);
    eventsRepository
      .getEventDetails(eventId, pleb.id)
      .then((e) => {
        if (e) {
          setEvent(e);
          setFormData({
            name: e.name,
            startDate: e.startDate ?? '',
            endDate: e.endDate ?? '',
            timeZone: e.timeZone ?? getDefaultTimeZone(),
            venue: e.venue ?? '',
            address: e.address ?? '',
            latitude: e.coordinates?.latitude?.toString() ?? '',
            longitude: e.coordinates?.longitude?.toString() ?? '',
            description: e.description ?? '',
          });
        }
      })
      .finally(() => setLoading(false));
  }, [eventId, pleb?.id]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (placeWrapperRef.current && !placeWrapperRef.current.contains(e.target as Node)) {
        setShowPlaceResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const startDateTZ = useMemo(() => {
    return new TZDate(formData.startDate, formData.timeZone);
  }, [formData.startDate, formData.timeZone]);

  const endDateTZ = useMemo(() => {
    return new TZDate(formData.endDate, formData.timeZone);
  }, [formData.endDate, formData.timeZone]);

  const fetchPlacePredictions = (input: string) => {
    if (!input.trim()) {
      setPlacePredictions([]);
      return;
    }
    eventsRepository
      .addressAutocomplete(input.trim())
      .then((items) => setPlacePredictions(items))
      .catch(() => setPlacePredictions([]));
  };

  const handlePlaceInputChange = (value: string) => {
    setPlaceSearchValue(value);
    setShowPlaceResults(true);
    fetchPlacePredictions(value);
  };

  const handlePlaceSelect = (prediction: { placeId: string; name: string }) => {
    eventsRepository
      .placeDetails(prediction.placeId)
      .then((details) => {
        setFormData((prev) => ({
          ...prev,
          timeZone: details.timeZone ?? prev.timeZone,
          venue: details.venue ?? '',
          address: details.address ?? '',
          latitude: typeof details.lat === 'number' && !isNaN(details.lat) ? String(details.lat) : prev.latitude,
          longitude: typeof details.lng === 'number' && !isNaN(details.lng) ? String(details.lng) : prev.longitude,
        }));
        setPlaceSearchValue('');
        setPlacePredictions([]);
        setShowPlaceResults(false);
      })
      .catch(() => {});
  };

  const handleClearPlace = () => {
    setFormData((prev) => ({
      ...prev,
      venue: '',
      address: '',
      latitude: '',
      longitude: '',
    }));
    setPlaceSearchValue('');
    setPlacePredictions([]);
    setShowPlaceResults(false);
  };

  const hasPlaceSelected = !!(formData.venue || formData.address);

  const handleChange = (key: keyof EventFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const tz = formData.timeZone || getDefaultTimeZone();

  const handleSave = () => {
    if (!eventId || !pleb?.id) return;
    const title = formData.name.trim();
    const hasLocation = !!(formData.venue?.trim() || formData.address?.trim());
    if (!title) {
      openConfirm({
        title: 'Title is required',
        buttons: [{ label: 'OK', variant: ButtonVariant.primary }],
      });
      return;
    }
    if (!hasLocation) {
      openConfirm({
        title: 'Location is required',
        buttons: [{ label: 'OK', variant: ButtonVariant.primary }],
      });
      return;
    }
    const startDate = formData.startDate.trim();
    const endDate = formData.endDate.trim();
    if (!startDate) {
      openConfirm({
        title: 'Start date is required',
        buttons: [{ label: 'OK', variant: ButtonVariant.primary }],
      });
      return;
    }
    if (!endDate) {
      openConfirm({
        title: 'End date is required',
        buttons: [{ label: 'OK', variant: ButtonVariant.primary }],
      });
      return;
    }
    const payload: EventUpdateDto = {};
    payload.name = title;
    payload.startDate = startDate;
    payload.endDate = endDate;
    payload.timeZone = formData.timeZone.trim() || undefined;
    payload.venue = formData.venue.trim();
    payload.address = formData.address.trim();
    payload.description = formData.description.trim();
    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);
    if (!isNaN(lat)) payload.latitude = lat;
    if (!isNaN(lng)) payload.longitude = lng;

    setSaving(true);
    eventsRepository
      .updateEvent(eventId, payload, pleb.id)
      .then((updated) => {
        setEvent(updated);
        navigate(`/home/search/event/${eventId}`);
      })
      .catch((error) => {
        openConfirm({
          title: error.response?.data?.message ?? 'Error saving event',
          buttons: [{ label: 'OK', variant: ButtonVariant.primary }],
        });
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex-1 px-4 pt-8">
        <TitleBar title="Edit Event" />
        <p className="text-brand-color-3">Event not found.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 px-4 pt-8 overflow-hidden flex flex-col gap-8 overflow-y-auto">
      <TitleBar title="Edit Event" />
      <div className="flex flex-col gap-2">
        <InputWithLabel
          label="TITLE"
          value={formData.name}
          onChange={(value) => handleChange('name', value)}
          maxLength={80}
        />
        <div ref={placeWrapperRef} className="relative border border-brand-color-3 rounded-md flex flex-row items-start justify-between">
          <div className="flex flex-col p-2 flex-1 min-w-0">
            <label className="font-semibold text-xs text-brand-color-3 cursor-text">LOCATION</label>
            {hasPlaceSelected ? (
              <div className="flex flex-col gap-0.5 mt-1">
                {formData.venue && (
                  <span className="font-semibold truncate" title={formData.venue}>
                    {formData.venue}
                  </span>
                )}
                {formData.address && (
                  <span className="text-sm text-brand-color-3 truncate" title={formData.address}>
                    {formData.address}
                  </span>
                )}
              </div>
            ) : (
              <input
                type="text"
                value={placeSearchValue}
                onChange={(e) => handlePlaceInputChange(e.target.value)}
                onFocus={() => placeSearchValue && setShowPlaceResults(true)}
                placeholder="Search for a venue or address..."
                className="w-full bg-transparent outline-none mt-1"
              />
            )}
          </div>
          {hasPlaceSelected && (
            <button
              type="button"
              onClick={handleClearPlace}
              className="p-2 text-brand-color-3 hover:opacity-80 focus:outline-none flex items-center justify-center shrink-0"
              aria-label="Clear venue and address"
            >
              <i className="material-icons">cancel</i>
            </button>
          )}
          {!hasPlaceSelected && showPlaceResults && placePredictions.length > 0 && (
            <ul className="absolute left-0 right-0 top-full mt-0 border border-brand-color-3 rounded-b-md bg-brand-bg-1 z-50 max-h-48 overflow-y-auto border-t-0 rounded-t-none">
              {placePredictions.map((p) => (
                <li
                  key={p.placeId}
                  role="button"
                  tabIndex={0}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handlePlaceSelect(p);
                  }}
                  className="px-3 py-2 cursor-pointer hover:bg-brand-bg-3 border-b border-brand-color-3 last:border-b-0"
                >
                  <span className="font-medium">{p.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <InputWithLabel
          label="DESCRIPTION"
          value={formData.description}
          onChange={(value) => handleChange('description', value)}
          type="textarea"
          maxLength={5000}
          rows={8}
        />
        <div className="border border-brand-color-3 rounded-md">
          <div className="flex flex-col p-2 flex-1">
            <label className="font-semibold text-xs text-brand-color-3 cursor-text">START DATE</label>
            <DatePicker
              selected={startDateTZ}
              onChange={(date: Date | null) => handleChange('startDate', date ? date.toISOString() : '')}
              timeZone={tz}
              showTimeSelect
              timeIntervals={TIME_STEP_MINUTES}
              timeCaption="Time"
              dateFormat="PPp"
              placeholderText="Select start date and time"
              className="w-full mt-1 p-0 border-0 bg-transparent outline-none"
              calendarClassName="react-datepicker--with-time"
            />
          </div>
        </div>
        <div className="border border-brand-color-3 rounded-md">
          <div className="flex flex-col p-2 flex-1">
            <label className="font-semibold text-xs text-brand-color-3 cursor-text">END DATE</label>
            <DatePicker
              selected={endDateTZ}
              onChange={(date: Date | null) => handleChange('endDate', date ? date.toISOString() : '')}
              minDate={startDateTZ ?? undefined}
              timeZone={tz}
              showTimeSelect
              timeIntervals={TIME_STEP_MINUTES}
              timeCaption="Time"
              dateFormat="PPp"
              placeholderText="Select end date and time"
              className="w-full mt-1 p-0 border-0 bg-transparent outline-none"
              calendarClassName="react-datepicker--with-time"
            />
          </div>
        </div>
      </div>
      <div className="mb-4 flex flex-col">
        <Button
          variant={ButtonVariant.primary}
          size={ButtonSize.medium}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default FormEditEvent;
